import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, InputNumber, Modal, Slider, Space, Typography } from 'antd';
import { resolveInitialModelPose, resolveModelPose } from '../../utils/orthoGlbMapLayer';

const { Text } = Typography;

const COORD_FIELDS = [
  { key: 'lng', label: '经度', step: 0.00001, precision: 6 },
  { key: 'lat', label: '纬度', step: 0.00001, precision: 6 },
  { key: 'altitude', label: '海拔', step: 0.5, precision: 1 }
];

const SLIDER_FIELDS = [
  { key: 'rotateX', label: '绕 X', step: 1, precision: 1, slider: [-180, 180] },
  { key: 'rotateY', label: '绕 Y', step: 1, precision: 1, slider: [-180, 180] },
  { key: 'rotateZ', label: '绕 Z', step: 1, precision: 1, slider: [-180, 180] },
  { key: 'scale', label: '缩放', step: 0.01, precision: 3, min: 0.05, max: 5, slider: [0.05, 5] }
];

function poseFromResolved(p) {
  return {
    lng: Number(p.lng),
    lat: Number(p.lat),
    altitude: Number.isFinite(Number(p.altitude)) ? Number(p.altitude) : 0,
    rotateX: Number.isFinite(Number(p.rotateX)) ? Number(p.rotateX) : 0,
    rotateY: Number.isFinite(Number(p.rotateY)) ? Number(p.rotateY) : 0,
    rotateZ: Number.isFinite(Number(p.rotateZ)) ? Number(p.rotateZ) : 0,
    scale: Number.isFinite(Number(p.scale)) && Number(p.scale) > 0 ? Number(p.scale) : 1
  };
}

/**
 * 指挥台 3D 位姿微调：可拖动浮动窗 + 三轴箭头
 */
export default function OrthoModelPoseModal({
  open,
  layer,
  saving,
  mapActive,
  onCancel,
  onPreview,
  onSave
}) {
  const baseline = useMemo(() => {
    const p = resolveModelPose(layer);
    return p ? poseFromResolved(p) : null;
  }, [layer]);

  const factory = useMemo(() => {
    const p = resolveInitialModelPose(layer);
    return p ? poseFromResolved(p) : null;
  }, [layer]);

  const [draft, setDraft] = useState(baseline || {});
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const offsetRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (!open) return;
    offsetRef.current = { x: 0, y: 0 };
    setOffset({ x: 0, y: 0 });
    if (!baseline) return;
    setDraft(baseline);
    onPreview?.(baseline);
  }, [open, baseline]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !layer?.id) return undefined;
    const onDrag = (ev) => {
      const d = ev?.detail;
      if (!d || Number(d.layerId) !== Number(layer.id)) return;
      setDraft((prev) => {
        const next = { ...prev, lng: d.lng, lat: d.lat };
        if (Number.isFinite(Number(d.altitude))) next.altitude = Number(d.altitude);
        onPreview?.(next);
        return next;
      });
    };
    window.addEventListener('zhxt-ortho-pose-drag', onDrag);
    return () => window.removeEventListener('zhxt-ortho-pose-drag', onDrag);
  }, [open, layer?.id, onPreview]);

  const applyDraft = (next) => {
    if (!next) return;
    setDraft(next);
    onPreview?.(next);
  };

  const patchField = (key, value) => {
    if (!Number.isFinite(Number(value))) return;
    applyDraft({ ...draft, [key]: Number(value) });
  };

  const nudge = (key, delta) => {
    const cur = Number(draft[key]);
    if (!Number.isFinite(cur)) return;
    patchField(key, cur + delta);
  };

  const onTitlePointerDown = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest?.('.ant-modal-close, button')) return;
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const orig = offsetRef.current;
    const move = (ev) => {
      const next = { x: orig.x + ev.clientX - startX, y: orig.y + ev.clientY - startY };
      offsetRef.current = next;
      setOffset(next);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const modelName = String(layer?.name || '').trim() || (layer?.id != null ? `#${layer.id}` : '');
  const titleText = modelName ? `3D 位姿微调 · ${modelName}` : '3D 位姿微调';

  return (
    <Modal
      open={open}
      title={(
        <div
          className="zhxt-ortho-pose-drag-title"
          title="按住标题可拖动窗口"
          onPointerDown={onTitlePointerDown}
        >
          {titleText}
        </div>
      )}
      width={360}
      destroyOnClose
      mask={false}
      maskClosable={false}
      wrapClassName="zhxt-ortho-pose-modal"
      style={{ top: 72, paddingBottom: 0 }}
      modalRender={(node) => (
        <div
          className="zhxt-ortho-pose-float"
          style={{ transform: `translate(${offset.x}px, ${offset.y}px)` }}
          onPointerDown={(e) => {
            if (e.target.closest('.ant-modal-header') && !e.target.closest('.ant-modal-close')) {
              onTitlePointerDown(e);
            }
          }}
        >
          {node}
        </div>
      )}
      onCancel={onCancel}
      footer={(
        <div className="zhxt-ortho-pose-footer">
          <Button
            size="small"
            disabled={!factory}
            onClick={() => applyDraft(factory)}
          >
            初始化
          </Button>
          <Space size={8}>
            <Button size="small" disabled={!baseline} onClick={() => applyDraft(baseline)}>
              恢复已保存
            </Button>
            <Button size="small" onClick={onCancel}>取消</Button>
            <Button type="primary" size="small" loading={saving} onClick={() => onSave?.(draft)}>
              保存到位姿
            </Button>
          </Space>
        </div>
      )}
    >
      {!mapActive && (
        <Text type="warning" style={{ display: 'block', marginBottom: 8 }}>
          建议先勾选叠加该 3D，再微调。
        </Text>
      )}
      <div className="zhxt-ortho-pose-step">
        <div className="zhxt-ortho-pose-step-row">
          <Button size="small" onClick={() => nudge('lng', -0.00005)}>西 ←</Button>
          <Button size="small" onClick={() => nudge('lng', 0.00005)}>东 →</Button>
          <Button size="small" onClick={() => nudge('lat', 0.00005)}>北 ↑</Button>
          <Button size="small" onClick={() => nudge('lat', -0.00005)}>南 ↓</Button>
        </div>
        <div className="zhxt-ortho-pose-step-row">
          <Button size="small" onClick={() => nudge('rotateX', -5)}>X -5°</Button>
          <Button size="small" onClick={() => nudge('rotateX', 5)}>X +5°</Button>
          <Button size="small" onClick={() => nudge('rotateY', -5)}>Y -5°</Button>
          <Button size="small" onClick={() => nudge('rotateY', 5)}>Y +5°</Button>
          <Button size="small" onClick={() => nudge('rotateZ', -5)}>Z -5°</Button>
          <Button size="small" onClick={() => nudge('rotateZ', 5)}>Z +5°</Button>
        </div>
        <div className="zhxt-ortho-pose-step-row">
          <Button size="small" onClick={() => nudge('altitude', 1)}>升高</Button>
          <Button size="small" onClick={() => nudge('altitude', -1)}>降低</Button>
          <Button size="small" onClick={() => nudge('scale', 0.05)}>放大</Button>
          <Button size="small" onClick={() => nudge('scale', -0.05)}>缩小</Button>
        </div>
      </div>
      <div className="zhxt-ortho-pose-coords">
        {COORD_FIELDS.map((f) => (
          <label key={f.key} className="zhxt-ortho-pose-coord">
            <span>{f.label}</span>
            <InputNumber
              size="small"
              style={{ width: '100%' }}
              step={f.step}
              precision={f.precision}
              value={Number(draft[f.key])}
              onChange={(v) => patchField(f.key, v)}
            />
          </label>
        ))}
      </div>
      <div className="zhxt-ortho-pose-sliders">
        {SLIDER_FIELDS.map((f) => (
          <div key={f.key} className="zhxt-ortho-pose-slider-row">
            <span className="zhxt-ortho-pose-slider-label">{f.label}</span>
            <Slider
              min={f.slider[0]}
              max={f.slider[1]}
              step={f.step}
              value={Number(draft[f.key])}
              onChange={(v) => patchField(f.key, v)}
            />
            <InputNumber
              size="small"
              style={{ width: 62 }}
              step={f.step}
              precision={f.precision}
              min={f.min}
              max={f.max}
              value={Number(draft[f.key])}
              onChange={(v) => patchField(f.key, v)}
            />
          </div>
        ))}
      </div>
    </Modal>
  );
}
